/**
 * dailyAttendanceApi.js
 * --------------------
 * API client for Education > Attendance > Daily Attendance submodule.
 * Connects directly to FastAPI backend with zero mock data.
 */
import api from '../../../../api/client'

export async function fetchDailyAttendance(dateString, filters = {}, sortBy = 'rollNo', sortOrder = 'asc') {
  const targetDate = dateString || new Date().toISOString().split('T')[0]

  const params = {
    date: targetDate,
  }

  if (filters.academicYear && filters.academicYear !== 'All') {
    params.academic_year = filters.academicYear
  }
  if (filters.branch && filters.branch !== 'All' && !isNaN(filters.branch)) {
    params.branch_id = parseInt(filters.branch, 10)
  }
  if (filters.course && filters.course !== 'All' && !isNaN(filters.course)) {
    params.course_id = parseInt(filters.course, 10)
  }
  if (filters.batch && filters.batch !== 'All' && !isNaN(filters.batch)) {
    params.batch_id = parseInt(filters.batch, 10)
  }
  if (filters.subject && filters.subject !== 'All' && !isNaN(filters.subject)) {
    params.subject_id = parseInt(filters.subject, 10)
  }
  if (filters.school && filters.school !== 'All' && !isNaN(filters.school)) {
    params.school_id = parseInt(filters.school, 10)
  }

  // Order by mapping (1: Institute, 2: Course, 3: FullName, 4: Batch, 5: RollNo)
  let order_by = 3
  if (sortBy === 'rollNo') order_by = 5
  else if (sortBy === 'fullName') order_by = 3
  else if (sortBy === 'courseName') order_by = 2
  else if (sortBy === 'batchNames') order_by = 4
  else if (sortBy === 'instituteName') order_by = 1
  params.order_by = order_by

  try {
    const { data } = await api.get('/attendance/daily', { params })
    const summary = data?.summary || {}
    const rawStudents = data?.students || []

    const students = rawStudents.map((s) => ({
      id: s.student_id,
      recordId: s.id,
      studentId: `STU-${String(s.student_id).padStart(4, '0')}`,
      fullName: s.full_name,
      rollNo: s.roll_no ? String(s.roll_no) : '',
      instituteName: s.institute_name,
      branchName: s.branch_name,
      courseName: s.course_name,
      batchNames: s.batch_names,
      attendance: s.attendance,
      reason: s.reason || '',
      swipes: s.swipes > 0 ? `${s.swipes} punch${s.swipes > 1 ? 'es' : ''}` : '',
    }))

    const totalStudents = summary.total ?? students.length
    const presentCount = summary.present ?? 0
    const absentCount = summary.absent ?? 0
    const holidayCount = summary.holiday ?? 0
    const presentPct = totalStudents > 0 ? Math.round((presentCount / totalStudents) * 100) : 0
    const absentPct = totalStudents > 0 ? Math.round((absentCount / totalStudents) * 100) : 0

    return {
      date: data?.date || targetDate,
      students,
      metrics: {
        totalStudents,
        presentCount,
        absentCount,
        holidayCount,
        presentPct,
        absentPct,
      },
    }
  } catch (err) {
    console.error('Failed to fetch daily attendance from backend', err)
    throw err
  }
}

export async function saveDailyAttendance(dateString, attendanceMap) {
  const targetDate = dateString || new Date().toISOString().split('T')[0]

  const entries = Object.entries(attendanceMap).map(([studentId, data]) => ({
    student_id: parseInt(studentId, 10),
    attendance: typeof data === 'string' ? data : data.attendance || 'P',
    reason: typeof data === 'object' ? data.reason || '' : '',
  }))

  const { data } = await api.post('/attendance/daily', {
    date: targetDate,
    entries,
  })

  return { success: true, savedCount: data?.saved_count || entries.length }
}

export async function syncSwipesForDate(dateString, students) {
  // If student has biometric punches recorded, mark 'P', otherwise 'A'
  const updatedMap = {}
  students.forEach((s) => {
    const hasSwipe = Boolean(s.swipes && s.swipes.trim().length > 0)
    updatedMap[s.id] = {
      attendance: hasSwipe ? 'P' : 'A',
      reason: hasSwipe ? '' : 'No swipe record found on biometric device',
    }
  })
  await saveDailyAttendance(dateString, updatedMap)
  return updatedMap
}

export async function syncLecturesForDate(dateString, students) {
  const updatedMap = {}
  students.forEach((s) => {
    updatedMap[s.id] = {
      attendance: 'P',
      reason: '',
    }
  })
  await saveDailyAttendance(dateString, updatedMap)
  return updatedMap
}
